import moment from 'moment-timezone';

export const getUTCDate = (value, format = 'YYYY-MM-DDTHH:mm:ss') => (value ? moment.utc(value).format(format) : null);

const getNormalizedSelectedTime = (selectedTime) => {
  if (typeof selectedTime !== 'string' || !selectedTime.trim()) {
    return '00:00:00';
  }

  const timeParts = selectedTime.trim().split(':');
  const [rawHour = '00', rawMinute = '00', rawSecond = '00'] = timeParts;
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  const second = Number(rawSecond);
  const isValidTime =
    timeParts.length >= 2 &&
    timeParts.length <= 3 &&
    timeParts.every((part) => /^\d+$/.test(part)) &&
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59 &&
    second >= 0 &&
    second <= 59;

  if (!isValidTime) {
    return '00:00:00';
  }

  return [rawHour.padStart(2, '0'), rawMinute.padStart(2, '0'), rawSecond.padStart(2, '0')].join(':');
};

export const buildUtcDateTime = ({ dateValue, timeValue, defaultTime, timezone }) => {
  if (!dateValue) {
    return null;
  }

  const selectedDate = dateValue.format('YYYY-MM-DD');
  const selectedTime = getNormalizedSelectedTime(timeValue ? timeValue.format('HH:mm:ss') : defaultTime);

  if (timezone) {
    return moment
      .tz(`${selectedDate} ${selectedTime}`, 'YYYY-MM-DD HH:mm:ss', timezone)
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss[Z]');
  }

  const [hour, minute, second] = selectedTime.split(':').map(Number);

  return dateValue.clone().set({ hour, minute, second }).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
};

export const getFormattedDate = (value, format = 'YYYY-MM-DDTHH:mm:ss') =>
  value ? moment.parseZone(value).format(format) : null;

export const getTimeRangeLabel = (
  startValue,
  endValue,
  { includeDateForMultiDay = false, includeDayOffset = false, timeFormat = 'hh:mm A', dateFormat = 'MMM D' } = {},
) => {
  if (!startValue && !endValue) return '';
  const start = startValue ? moment.parseZone(startValue) : null;
  const end = endValue ? moment.parseZone(endValue) : null;
  if (!start || !end) {
    const single = start || end;
    return single ? single.format(timeFormat) : '';
  }
  const sameDay = start.isSame(end, 'day');
  if (includeDateForMultiDay && !sameDay) {
    return `${start.format(`${dateFormat}, ${timeFormat}`)} - ${end.format(`${dateFormat}, ${timeFormat}`)}`;
  }
  const base = `${start.format(timeFormat)} - ${end.format(timeFormat)}`;
  if (includeDayOffset && !sameDay) {
    const dayDiff = end.startOf('day').diff(start.startOf('day'), 'days');
    return `${base} (+${dayDiff} day${dayDiff > 1 ? 's' : ''})`;
  }
  return base;
};

export const getFormattedDateNoParseZone = (value, format = 'YYYY-MM-DDTHH:mm:ss') =>
  value ? moment(value).format(format) : null;

export const getFormattedDateUnix = (columnDate, format) =>
  columnDate != null && columnDate ? moment.unix(columnDate).format(format) : '-';

export const getFormattedDateWithTimeZone = (value, timezone, format = 'YYYY-MM-DDTHH:mm:ss') => {
  if (!timezone) {
    return getFormattedDate(value, format);
  }
  return value ? moment.tz(value, timezone).format(format) : null;
};

export const getMomentDate = (value) => (value ? moment.parseZone(value) : null);

export const disabledStartDate = (startDate, endDate) => {
  const oneDayBefore = moment().subtract(1, 'days').endOf('day');
  if (startDate && endDate) {
    return startDate >= moment(endDate).endOf('day') || startDate < oneDayBefore;
  }
  if (startDate) {
    return startDate < oneDayBefore;
  }
  return false;
};

export const disabledStartDateRelatedToTimeZone = (startDate, endDate, timezone) => {
  if (!timezone) {
    return false;
  }
  const oneDayBefore = moment.tz(timezone).subtract(1, 'days').endOf('day');
  if (startDate && endDate) {
    return startDate >= moment.tz(endDate, timezone).endOf('day') || startDate < oneDayBefore;
  }
  if (startDate) {
    return startDate < oneDayBefore;
  }
  return false;
};

export const disabledEndDate = (endDate, startDate) => {
  if (!endDate || !startDate) {
    return false;
  }
  return endDate <= moment(startDate).subtract(1, 'days').endOf('day');
};

export const disabledEndDateRelatedToTimeZone = (endDate, startDate, timezone) => {
  if (!timezone) {
    return false;
  }
  const oneDayBefore = moment.tz(timezone).subtract(1, 'days').endOf('day');
  if (startDate && endDate) {
    return endDate <= moment.tz(startDate, timezone).subtract(1, 'days').endOf('day');
  }
  if (endDate) {
    return endDate < oneDayBefore;
  }
  return false;
};

export const getDisabledDates = (pickerDate, startDate, endDate) => {
  if (pickerDate) {
    const oneDayBefore = moment().subtract(1, 'days').endOf('day');
    if (startDate && endDate) {
      return (
        pickerDate < moment(startDate).endOf('day').subtract(1, 'days') || pickerDate > moment(endDate).endOf('day')
      );
    }
    if (startDate) {
      return pickerDate < moment(startDate).endOf('day').subtract(1, 'days') || pickerDate < oneDayBefore;
    }
    if (endDate) {
      return pickerDate > moment(endDate).endOf('day') || pickerDate < oneDayBefore;
    }
    return pickerDate < oneDayBefore;
  }
  return false;
};

export const humanizeTimestamp = (timestamp) => {
  const time = moment.utc(timestamp); // parse time as UTC
  const now = moment(); // Not in future humanized time
  return moment.min(time, now).from(now);
};

export const getMomentDateWithSpecificTimeZone = (dateTime, timezone) => {
  if (!timezone) {
    timezone = moment.tz.guess();
  }
  return dateTime && timezone ? moment.tz(dateTime, timezone) : null;
};

export const getMomentDateAppendSpecificTimeZone = (dateTime, timezone) => {
  if (!timezone) {
    timezone = moment.tz.guess();
  }
  return dateTime && timezone ? moment(dateTime).tz(timezone) : null;
};

export const defaultTimeZone = () => moment.tz.guess();

export const getDateTimeDiff = (dateTime) => {
  const diff = moment.parseZone(dateTime).unix() - moment.parseZone().unix();
  return diff > 0 ? diff : 0;
};

export const isNowBeforeDate = (date, param = 'day') => moment().isBefore(moment(date).startOf(param));

export const getRelativeDateFromTimeZone = (utcDateTime, siteTimezone) => {
  if (!utcDateTime) return '';
  const updatedTime = moment.utc(utcDateTime).tz(siteTimezone);
  const now = moment().tz(siteTimezone);

  if (now.isSame(updatedTime, 'day')) {
    return updatedTime.format('HH:mm');
  }
  if (now.clone().subtract(1, 'day').isSame(updatedTime, 'day')) {
    return 'Yesterday';
  }
  return updatedTime.format('DD MMM YY');
};

export const getFormattedTimeFromSeconds = (seconds) => {
  if (!seconds) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h ? `${h}h` : null, m ? `${m}m` : null, `${s}s`].filter(Boolean).join(' ');
};
